import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, Printer, FileDown, Copy, ChevronLeft, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { branchService } from '@/services/branchService'
import { financeService } from '@/services/financeService'
import { reportService } from '@/services/reportService'
import { BranchEntity } from '@/api/types'
import { FinanceCustomer } from '@/api/types'

interface CustomerLedgerEntry {
  account: string | null
  customer: string | null
  costCenter: string | null
  costLocation: string | null
  vendor: string | null
  accountCode: string | null
  description: string
  voucher: number
  type: string | null
  debit: number
  credit: number
  balance: number
  project: string | null
  ref: string | null
  department: string | null
  notes: string | null
  createdDate: string
  branchName: string | null
}

export const CustomerLedgerReport: React.FC = () => {
  const { t } = useTranslation('common')
  const { user } = useAuthStore()
  const navigate = useNavigate()

  const [ledgerData, setLedgerData] = useState<CustomerLedgerEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [branches, setBranches] = useState<BranchEntity[]>([])
  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [searchQuery, setSearchQuery] = useState('')

  // Pagination states
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  // Filter states
  const [fromDate, setFromDate] = useState<string>('')
  const [toDate, setToDate] = useState<string>('')
  const [selectedBranchId, setSelectedBranchId] = useState<number | undefined>(undefined)
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(undefined)

  // Load branches and set default branch
  useEffect(() => {
    const loadBranches = async () => {
      try {
        const branchList = await branchService.getAllBranches()
        setBranches(branchList || [])
        // Set default branch to user's selected branch if available and not already set
        // Only set after branches are loaded to ensure options exist
        if (user?.selectedBranch?.branchId && !selectedBranchId) {
          // Verify the branch exists in the loaded list
          // BranchEntity uses 'id', user.selectedBranch uses 'branchId'
          const branchExists = branchList?.some(b => b.id === user.selectedBranch?.branchId)
          if (branchExists) {
            setSelectedBranchId(user.selectedBranch.branchId)
          }
        }
      } catch (error: any) {
        // Error handled silently
      }
    }
    loadBranches()
  }, [])

  // Load customers when branch is selected
  useEffect(() => {
    if (!selectedBranchId) {
      setCustomers([])
      setSelectedCustomerId(undefined)
      return
    }

    const loadCustomers = async () => {
      try {
        const customerList = await financeService.getAllCustomers(selectedBranchId)
        setCustomers(customerList || [])
        // Reset customer selection to "All Customers" when branch changes
        setSelectedCustomerId(undefined)
      } catch (error: any) {
        setCustomers([])
      }
    }
    loadCustomers()
  }, [selectedBranchId])

  // Set default dates (current month)
  useEffect(() => {
    const today = new Date()
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    
    setFromDate(formatDateForInput(firstDay))
    setToDate(formatDateForInput(lastDay))
  }, [])

  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const handleSearch = async (resetPage: boolean = false) => {
    if (!selectedBranchId) {
      toast.error('Please select a branch')
      return
    }

    if (!fromDate || !toDate) {
      toast.error('Please select both from date and to date')
      return
    }

    try {
      setLoading(true)
      // Convert dates to ISO date-time format (YYYY-MM-DDTHH:mm:ss)
      const fromDateTime = `${fromDate}T00:00:00`
      const toDateTime = `${toDate}T23:59:59`
      
      // If customer is not selected (All Customers), send customerId as 0
      const customerId = selectedCustomerId !== undefined ? selectedCustomerId : 0
      
      const currentPage = resetPage ? 1 : pageNumber
      if (resetPage) {
        setPageNumber(1)
      }
      
      const response = await reportService.getCustomerLedgerReport({
        fromDate: fromDateTime,
        toDate: toDateTime,
        branchId: selectedBranchId,
        pageNumber: currentPage,
        pageSize: pageSize,
        customerId: customerId,
      })
      setLedgerData(response.data || [])
      setTotalCount(response.totalRecords || 0)
      const pages = response.totalRecords ? Math.ceil(response.totalRecords / pageSize) : 0
      setTotalPages(pages)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load customer ledger report')
      setLedgerData([])
      setTotalCount(0)
      setTotalPages(0)
    } finally {
      setLoading(false)
    }
  }

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return ledgerData
    const query = searchQuery.toLowerCase()
    return ledgerData.filter((entry) => {
      return (
        (entry.customer || '').toLowerCase().includes(query) ||
        (entry.description || '').toLowerCase().includes(query) ||
        (entry.ref || '').toLowerCase().includes(query) ||
        (entry.voucher?.toString() || '').includes(query)
      )
    })
  }, [ledgerData, searchQuery])

  // Calculate totals
  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, entry) => {
        acc.debit += entry.debit || 0
        acc.credit += entry.credit || 0
        return acc
      },
      { debit: 0, credit: 0 }
    )
  }, [filteredData])

  // Handle page size change
  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setPageNumber(1)
    // Trigger search with new page size
    if (selectedBranchId && fromDate && toDate) {
      setTimeout(() => handleSearch(true), 0)
    }
  }

  // Handle page change
  useEffect(() => {
    if (selectedBranchId && fromDate && toDate && pageNumber > 1 && ledgerData.length > 0) {
      handleSearch(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber])

  const branchOptions: SearchableSelectOption[] = branches.map((branch) => {
    const branchId = branch.id || (branch as any).branchId || 0
    return {
      value: String(branchId),
      label: branch.nameEn || branch.nameAr || `Branch ${branchId}`,
    }
  })

  const customerOptions: SearchableSelectOption[] = [
    { value: '', label: t('reports.allCustomers') },
    ...customers.map((customer) => ({
      value: String(customer.customerId),
      label: customer.customerNameEn || customer.customerNameAr || `Customer ${customer.customerId}`,
    })),
  ]

  const handleBranchChange = (value: string) => {
    setSelectedBranchId(value ? Number(value) : undefined)
    setSelectedCustomerId(undefined)
  }

  const handleCustomerChange = (value: string) => {
    // When "All Customers" is selected (empty string), set to undefined so we can send 0 to API
    setSelectedCustomerId(value ? Number(value) : undefined)
  }

  const handlePrint = () => {
    if (!selectedBranchId || !fromDate || !toDate) {
      toast.error('Please select branch and date range before printing')
      return
    }
    
    // Navigate to print page with query parameters
    const params = new URLSearchParams({
      fromDate,
      toDate,
      branchId: String(selectedBranchId),
    })
    if (selectedCustomerId) {
      params.append('customerId', String(selectedCustomerId))
    }
    
    window.open(`/reports/customer-ledger/print?${params.toString()}`, '_blank')
  }

  const handleCopy = () => {
    toast.success('Copy functionality coming soon')
  }

  const handleCsv = () => {
    if (filteredData.length === 0) {
      toast.error('No data to export')
      return
    }

    // Create CSV content
    const headers = [
      'Date',
      'Customer',
      'Voucher',
      'Description',
      'Project',
      'Cost Profit Center',
      'Cost Location',
      'Reference',
      'Notes',
      'Debit',
      'Credit',
      'Balance',
    ].join(',')

    const rows = filteredData.map(entry => [
      `"${formatDate(entry.createdDate)}"`,
      `"${entry.customer || ''}"`,
      entry.voucher || '',
      `"${entry.description || ''}"`,
      `"${entry.project || ''}"`,
      `"${entry.costCenter || ''}"`,
      `"${entry.costLocation || ''}"`,
      `"${entry.ref || ''}"`,
      `"${entry.notes || ''}"`,
      `"${formatAmount(entry.debit)}"`,
      `"${formatAmount(entry.credit)}"`,
      `"${formatAmount(entry.balance)}"`,
    ].join(','))

    // Add total row
    const totalRow = [
      '"Total"',
      '""',
      '""',
      '""',
      '""',
      '""',
      '""',
      '""',
      '""',
      `"${formatAmount(totals.debit)}"`,
      `"${formatAmount(totals.credit)}"`,
      `"${formatAmount(totals.debit - totals.credit)}"`,
    ].join(',')

    const csvContent = [headers, ...rows, totalRow].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    const dateStr = fromDate && toDate ? `${fromDate}_${toDate}` : 'report'
    link.setAttribute('download', `customer-ledger-${dateStr}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success('CSV file downloaded')
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('navigation.customerLedgerReport')}
        description={t('reports.viewCustomerLedgerEntries') || 'View customer ledger entries and transactions'}
        action={{
          label: t('reports.print'),
          onClick: handlePrint,
          icon: Printer,
        }}
      />

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>{t('reports.fromDate')}</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.toDate')}</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.branch')}</Label>
              <SearchableSelect
                options={branchOptions}
                value={selectedBranchId ? String(selectedBranchId) : ''}
                onValueChange={handleBranchChange}
                placeholder={t('reports.selectBranch')}
                searchPlaceholder={t('reports.searchBranches')}
                emptyMessage={t('reports.noBranchesFound')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.customer')}</Label>
              <SearchableSelect
                options={customerOptions}
                value={selectedCustomerId ? String(selectedCustomerId) : ''}
                onValueChange={handleCustomerChange}
                placeholder={t('reports.allCustomers')}
                searchPlaceholder={t('reports.searchCustomers')}
                emptyMessage={t('reports.noCustomersFound')}
                disabled={!selectedBranchId}
              />
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <Label>{t('reports.search')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('reports.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => handleSearch(true)} disabled={loading}>
                {loading ? t('reports.loading') : t('reports.search')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="h-4 w-4 mr-2" />
                {t('reports.copy')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCsv}>
                <FileDown className="h-4 w-4 mr-2" />
                {t('reports.csv')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading report...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title="No data found"
                description="Click Search to load customer ledger report"
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('reports.date')}</TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('reports.customer')}</TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('reports.voucher')}</TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('reports.description')}</TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('reports.project')}</TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('reports.costProfitCenter')}</TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('reports.costLocation')}</TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('reports.reference')}</TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('reports.notes')}</TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('reports.debit')}</TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('reports.credit')}</TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('reports.balance')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((entry, index) => (
                      <TableRow
                        key={index}
                        className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                      >
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatDate(entry.createdDate)}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{entry.customer || '-'}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {entry.voucher && entry.voucher > 0 ? (
                            <button
                              onClick={() => navigate(`/general-entries/${entry.voucher}/view`, { state: { readOnly: true } })}
                              className="text-primary hover:underline cursor-pointer"
                            >
                              {entry.voucher}
                            </button>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{entry.description || '-'}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{entry.project || '-'}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{entry.costCenter || '-'}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{entry.costLocation || '-'}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{entry.ref || '-'}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{entry.notes || '-'}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatAmount(entry.debit)}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatAmount(entry.credit)}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatAmount(entry.balance)}</TableCell>
                      </TableRow>
                    ))}
                    {/* Total Row */}
                    <TableRow className="border-t-2 border-primary/20 bg-primary/5 font-bold">
                      <TableCell colSpan={9} className="px-4 py-2 text-sm text-center align-middle">
                        Total
                      </TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatAmount(totals.debit)}</TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatAmount(totals.credit)}</TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatAmount(totals.debit - totals.credit)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="border-t px-4 sm:px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground text-center sm:text-left">
                  Showing {(pageNumber - 1) * pageSize + 1} to{' '}
                  {Math.min(pageNumber * pageSize, totalCount)} of {totalCount} entries
                </div>
                <div className="flex items-center gap-3 flex-wrap justify-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Page Size:</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                    disabled={pageNumber === 1 || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline ml-1">Previous</span>
                  </Button>
                  <div className="text-sm whitespace-nowrap px-2">
                    Page {pageNumber} of {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.min(prev + 1, totalPages || 1))}
                    disabled={pageNumber >= totalPages || loading}
                  >
                    <span className="hidden sm:inline mr-1">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

